import React, { useCallback, useEffect, useRef, useState } from "react";
import Icons from "../icons";
import { issueStatusNote, JiraStatusBadge, PriorityBadge, TRIAL_LABEL } from "../jiraBadges";
import { defaultProjectOf } from "../defaultProject";
import BoardSearch, { ARTIFACT_SCOPE } from "./BoardSearch";
import HelpButton from "./HelpDialog";
import Loader from "./Loader";
import ExecutionTicketsModal from "./ExecutionTicketsModal";
import RaiseBugModal from "./RaiseBugModal";
import TestRunsPanel, {
  COLUMN_HEAD,
  DeleteSteps,
  deleteMessage,
  deleteSteps,
  logOption,
  NO_STORY,
  storyOfTest,
  useSteps,
} from "./TestRunsPanel";
import ExecutionResultView from "./ExecutionResultView";
import ConfirmModal from "./ConfirmModal";
import { DIALOG_HEIGHT } from "./dialogSize";
import TestCasePicker from "./TestCasePicker";
import { useToast } from "./Toast";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

function apiWsUrl(path) {
  const base = new URL(API_BASE_URL, window.location.origin);
  const proto = base.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${base.host}${path}`;
}

const RESULT_OPTIONS = ["not_run", "passed", "failed", "blocked", "skipped"];

function resultChip(status) {
  switch (status) {
    case "passed":
      return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
    case "failed":
      return "bg-rose-50 text-rose-700 ring-rose-600/20";
    case "blocked":
      return "bg-amber-50 text-amber-700 ring-amber-600/20";
    case "skipped":
      return "bg-slate-100 text-slate-600 ring-slate-500/20";
    default:
      return "bg-slate-50 text-slate-500 ring-slate-400/20";
  }
}

function statusChip(status) {
  switch (status) {
    case "in_progress":
      return "bg-blue-50 text-blue-700 ring-blue-600/20";
    case "completed":
      return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
    default:
      return "bg-slate-100 text-slate-600 ring-slate-500/20";
  }
}

/* A short human label for a test-case step. */
function describeStep(s) {
  if (!s) return "";
  if (s.description) return s.description;
  const label = s.field_label || s.selector || s.value || "";
  return [s.action, label].filter(Boolean).join(" ").trim() || s.action || "step";
}

/* Colour for a per-step status dot. */
function stepDot(status) {
  switch (status) {
    case "passed":
      return "bg-emerald-500";
    case "failed":
      return "bg-rose-500";
    case "running":
      return "bg-amber-500 animate-pulse";
    default:
      return "bg-slate-300";
  }
}

/* Full-screen dialog for a single step screenshot (styled like the board's
   run view). The <img> src is only mounted here, so the API fetch for the
   screenshot happens lazily when the user clicks View. */
function ScreenshotDialog({ src, caption, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex max-h-full min-w-0 max-w-6xl flex-col overflow-hidden rounded-xl bg-slate-900 shadow-2xl ring-1 ring-slate-700">
        <div className="flex items-center justify-between gap-3 border-b border-slate-700 px-4 py-2 text-sm text-slate-200">
          <span className="truncate">{caption}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close screenshot"
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-black">
          <img src={src} alt={caption} className="mx-auto block h-auto max-w-full" />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  List screen helpers                                                */
/* ------------------------------------------------------------------ */
const NO_EPIC = "__no_epic__";
const SPACE_STORAGE_KEY = "cortexqa_exec_space";
const ACTIVE_RUN = new Set(["queued", "running"]);

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}


function readStoredSpace() {
  try {
    return localStorage.getItem(SPACE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function storeSpace(key) {
  try {
    localStorage.setItem(SPACE_STORAGE_KEY, key);
  } catch {
    /* per-viewer convenience only */
  }
}

/* Which space (Jira project) an issue key belongs to: "CTO-232" → "CTO". */
function spaceOf(key) {
  const k = String(key || "");
  const i = k.lastIndexOf("-");
  return i > 0 ? k.slice(0, i).toUpperCase() : "";
}

/* The epic an execution belongs to — stored on one-shot runs, otherwise the
   first linked ticket's epic (or the ticket itself when it is the epic).
   Linked to tickets under no epic: NO_EPIC. Linked to nothing: null (the
   screen doesn't list it; the top search still opens it). */
function epicOfExecution(ex) {
  if (ex.epic_key) return ex.epic_key;
  for (const t of ex.tickets || []) {
    if (t.epic_key) return t.epic_key;
    if (/epic/i.test(t.issue_type || "")) return t.ticket_key;
  }
  return (ex.tickets || []).length ? NO_EPIC : null;
}

/* Status chip for an execution: the runner's state when it has one, else
   the rolled-up case results of a hand-built execution. */
function runChip(ex) {
  const live = ex.live;
  switch (ex.run_status) {
    case "queued":
      return {
        label: live?.queue_position ? `Queued #${live.queue_position}` : "Queued",
        cls: "bg-slate-100 text-slate-600 ring-slate-500/20",
        dot: "bg-slate-400",
      };
    case "running":
      if (live?.state === "waiting_input")
        return { label: "Needs input", cls: "bg-amber-50 text-amber-800 ring-amber-600/30", dot: "bg-amber-500 animate-pulse" };
      return {
        label: live?.total ? `Running ${live.step}/${live.total}` : "Running",
        cls: "bg-blue-50 text-blue-700 ring-blue-600/20",
        dot: "bg-blue-500 animate-pulse",
      };
    case "passed":
      return { label: "Passed", cls: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", dot: "bg-emerald-500" };
    case "failed":
      return { label: "Failed", cls: "bg-rose-50 text-rose-700 ring-rose-600/20", dot: "bg-rose-500" };
    case "cancelled":
      return { label: "Stopped", cls: "bg-slate-100 text-slate-600 ring-slate-500/20", dot: "bg-slate-400" };
    case "interrupted":
      return { label: "Interrupted", cls: "bg-amber-50 text-amber-800 ring-amber-600/30", dot: "bg-amber-500" };
    case "error":
      return { label: "Error", cls: "bg-rose-50 text-rose-700 ring-rose-600/20", dot: "bg-rose-500" };
    default: {
      if (ex.failed > 0) return { label: "Failed", cls: "bg-rose-50 text-rose-700 ring-rose-600/20", dot: "bg-rose-500" };
      if (ex.total > 0 && ex.passed === ex.total)
        return { label: "Passed", cls: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", dot: "bg-emerald-500" };
      return {
        label: (ex.status || "draft").replace("_", " "),
        cls: `capitalize ${statusChip(ex.status)}`,
        dot: "bg-slate-400",
      };
    }
  }
}

/* ------------------------------------------------------------------ */
/*  Epic sidebar item                                                  */
/* ------------------------------------------------------------------ */
/* The stories of the epic's tree that hold Artifacts (one with none isn't
   listed), a card each: {key, summary, status, status_category, priority,
   count}. Artifacts filed under no story come last, as "No story" (NO_STORY). */
export function storyRowsOf(tree) {
  const rows = (tree?.stories || [])
    .map((s) => ({
      key: s.key || NO_STORY,
      summary: s.summary || "",
      status: s.status || "",
      status_category: s.status_category || "",
      priority: s.priority || "",
      count: (s.tests || []).filter((t) => (t.scripts || []).length).length,
    }))
    .filter((s) => s.count > 0);
  return [...rows.filter((s) => s.key !== NO_STORY), ...rows.filter((s) => s.key === NO_STORY)];
}

/* "Epic PSR-1971 is in draft." — the hover of an epic's status chip. */
export const epicStatusNote = (key, status) => issueStatusNote("Epic", key, status);

export function EpicSidebarItem({
  epicKey, epicSummary, status, statusCategory, priority, scriptCount, execCount, selected, onClick,
  /* The selected epic's stories (storyRowsOf), or null while they load. */
  stories = null, expanded = false, onToggle, selectedStoryKey = null, onSelectStory,
  /* The stories header's Refresh: the epic's tree again, with its Jira status. */
  onRefreshStories, refreshing = false,
}) {
  const special = epicKey === NO_EPIC;
  const empty = !special && scriptCount === 0 && execCount === 0;
  /* An epic whose Artifacts are filed under stories opens into them. */
  const branch = !special && scriptCount > 0 && !(selected && stories && stories.length === 0);
  const open = selected && expanded && branch;
  return (
    <div>
      <div
        className={[
          /* A card, as on the Requirement Board. */
          "flex w-full items-start rounded-xl border transition",
          selected
            ? "border-blue-400 bg-blue-50 ring-1 ring-blue-400/40"
            : "border-slate-200 bg-card hover:border-blue-200 hover:bg-blue-50/40",
        ].join(" ")}
      >
        {branch && (
          <button
            type="button"
            onClick={() => (selected ? onToggle?.() : onClick())}
            aria-label={open ? `Hide the stories of ${epicKey}` : `Show the stories of ${epicKey}`}
            aria-expanded={open}
            className="ml-1.5 mt-2.5 flex h-6 w-6 flex-none items-center justify-center rounded-md text-slate-500 transition hover:bg-white hover:text-slate-800"
          >
            {open ? <Icons.ChevronDown className="h-4 w-4" /> : <Icons.ChevronRight className="h-4 w-4" />}
          </button>
        )}
        <button type="button" onClick={onClick} className={`min-w-0 flex-1 py-3 pr-3 text-left ${branch ? "pl-1.5" : "pl-3"}`}>
          {/* No per-row icon: the column header carries the Epic icon. */}
          <span className="flex flex-wrap items-center gap-1.5">
            <span
              className={[
                "block text-[13px] font-semibold tracking-tight",
                special ? "font-normal italic text-slate-500" : selected ? "text-brand-700" : "text-brand-600",
              ].join(" ")}
            >
              {epicKey === NO_EPIC ? "No epic" : epicKey}
            </span>
            {/* The epic's Jira status and priority. */}
            {!special && <JiraStatusBadge status={status} category={statusCategory} title={status ? epicStatusNote(epicKey, status) : undefined} />}
            {!special && <PriorityBadge priority={priority} />}
          </span>
          {!special && (
            <span
              className={["mt-0.5 block truncate text-[12px]", empty ? "text-slate-400" : "text-slate-700"].join(" ")}
              title={epicSummary}
            >
              {epicSummary || "(no summary)"}
            </span>
          )}
        </button>
      </div>
      {/* Its stories, as a tree: a trunk under the epic's chevron, a branch
          to each story card. A story shows its Artifacts. */}
      {open && (
        <div role="group" aria-label={`Stories of ${epicKey}`}>
          {/* The stories' own header, as each column has: the Story icon,
              the count and Refresh. */}
          <div className="relative pl-7 pt-2">
            <span aria-hidden="true" className="absolute bottom-0 left-[18px] top-0 border-l-2 border-slate-300" />
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-100/70 px-2.5 py-1.5">
              <Icons.JiraStory />
              <span className="text-[12px] font-semibold text-slate-800">Stories</span>
              <span
                className="ml-auto rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-500 ring-1 ring-slate-200"
                aria-label={stories ? `${stories.length} ${stories.length === 1 ? "story" : "stories"}` : undefined}
              >
                {stories ? stories.length : "…"}
              </span>
              {onRefreshStories && (
                <button
                  type="button"
                  onClick={onRefreshStories}
                  disabled={refreshing}
                  title="Refresh the stories, their Artifacts and their Jira status"
                  aria-label={`Refresh the stories of ${epicKey}`}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-500 transition hover:bg-white hover:text-slate-800 disabled:opacity-60"
                >
                  <Icons.Refresh className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                </button>
              )}
            </div>
          </div>
          {stories === null ? (
            <p className="relative pl-7 pt-2 text-[11px] text-slate-400">
              <span aria-hidden="true" className="absolute left-[18px] top-0 h-4 w-2.5 rounded-bl-lg border-b-2 border-l-2 border-slate-300" />
              Loading stories…
            </p>
          ) : (
            <ul>
              {stories.map((s, i) => {
                const on = selectedStoryKey === s.key;
                const none = s.key === NO_STORY;
                const last = i === stories.length - 1;
                return (
                  <li key={s.key} className="relative pl-7 pt-2">
                    {/* └ to this card, and │ on to the next one. */}
                    <span aria-hidden="true" className="absolute left-[18px] top-0 h-7 w-2.5 rounded-bl-lg border-b-2 border-l-2 border-slate-300" />
                    {!last && <span aria-hidden="true" className="absolute bottom-0 left-[18px] top-7 border-l-2 border-slate-300" />}
                    <button
                      type="button"
                      onClick={() => onSelectStory?.(s.key)}
                      aria-current={on ? "true" : undefined}
                      className={[
                        /* A card, as the epic's. */
                        "block w-full rounded-xl border p-2.5 text-left transition",
                        on ? "border-blue-400 bg-blue-50 ring-1 ring-blue-400/40" : "border-slate-200 bg-card hover:border-blue-200 hover:bg-blue-50/40",
                      ].join(" ")}
                    >
                      <span className="flex items-center gap-1.5">
                        <span
                          className={`text-[12px] font-semibold tracking-tight ${none ? "font-normal italic text-slate-500" : on ? "text-brand-700" : "text-brand-600"}`}
                        >
                          {none ? "No story" : s.key}
                        </span>
                        {!none && <JiraStatusBadge status={s.status} category={s.status_category} title={issueStatusNote("Story", s.key, s.status)} />}
                        {!none && <PriorityBadge priority={s.priority} />}
                        <span
                          className="ml-auto flex-none rounded-full bg-white px-1.5 py-px text-[10px] font-semibold tabular-nums text-slate-500 ring-1 ring-slate-200"
                          title={`${s.count} ${s.count === 1 ? "Artifact" : "Artifacts"}`}
                          aria-label={`${s.count} ${s.count === 1 ? "Artifact" : "Artifacts"}`}
                        >
                          {s.count}
                        </span>
                      </span>
                      <span className="mt-0.5 block truncate text-[12px] text-slate-700" title={none ? undefined : s.summary}>
                        {none ? "Artifacts filed under no story" : s.summary || "(no summary)"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Hand-built execution row (No epic bucket)                          */
/* ------------------------------------------------------------------ */
function ExecutionListRow({ ex, onOpen, onDelete }) {
  const primaryTicket = ex.tickets?.[0] || null;
  const chip = runChip(ex);
  /* A For you run is evidence: never deleted from CortexQA. Every trial is. */
  const isFormal = ex.run_mode === "formal" || Boolean(ex.assigned_run);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(ex.executionkey)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(ex.executionkey)}
      className="group flex cursor-pointer items-center gap-4 px-5 py-3 transition-colors hover:bg-brand-50/50 focus:outline-none focus-visible:bg-brand-50/60"
    >
      <span className={`h-2.5 w-2.5 flex-none rounded-full ${chip.dot}`} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-800 group-hover:text-brand-800">{ex.name}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[11px] text-slate-500">
          <span className={`rounded-full px-1.5 py-px font-semibold ring-1 ring-inset ${chip.cls}`}>{chip.label}</span>
          <span>
            {ex.total - (ex.not_run || 0)}/{ex.total} run
          </span>
          {primaryTicket && <span className="font-mono text-violet-700">{primaryTicket.ticket_key}</span>}
          {ex.run_mode === "formal" && <span className="font-medium text-slate-500">Formal run: For you › Test Runs</span>}
        </div>
      </div>
      <span className="hidden w-28 flex-none text-[12px] tabular-nums text-slate-500 sm:block">{fmtDate(ex.createddate)}</span>
      {isFormal ? (
        <span className="w-8 flex-none" aria-hidden="true" />
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete({ executionkey: ex.executionkey, name: ex.name });
          }}
          className="flex w-8 flex-none justify-end rounded-md p-1.5 text-slate-300 opacity-0 transition hover:bg-rose-50 hover:text-rose-500 group-hover:opacity-100"
          title="Delete this trial run"
        >
          <Icons.Trash className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Top-level: space → epics → tests → runs                            */
/* ------------------------------------------------------------------ */
/* Module-level cache so navigating back from a detail view doesn't trigger a
   full reload. Cleared on explicit refresh or after a successful delete. */
let _executionsCache = null;

export default function ExecutionsView({ userkey, boards = [], initialExecutionKey = null, autoRun = false, initialTarget = null }) {
  const [executions, setExecutions] = useState(() => _executionsCache || []);
  const [, setLoading] = useState(!_executionsCache);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(initialExecutionKey);
  /* True when the selected execution was opened from a test's run card (→ result page). */
  const [selectedIsRun, setSelectedIsRun] = useState(false);
  const [autoRunKey, setAutoRunKey] = useState(autoRun ? initialExecutionKey : null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  /* "Delete the Log", off each time the confirmation opens. */
  const [deleteWithLog, setDeleteWithLog] = useState(false);
  /* The execution being deleted once confirmed: the loader lists its steps
     until the columns are fresh, then a toast says it went. */
  const [removing, setRemoving] = useState(null);
  const removal = useSteps();
  const askDeleteExecution = (target) => {
    setDeleteWithLog(false);
    setDeleteTarget(target);
  };
  const [showToast, toast] = useToast();
  /* A search result waiting for its epic's tests to load before the test can
     be selected: { epicKey, storyKey, testKey }. */
  const [pendingPick, setPendingPick] = useState(null);
  /* initialTarget ({ epicKey, storyKey, testKey }) — opened from "Save & Execute" on the Requirement Board. */
  const [selectedEpicKey, setSelectedEpicKey] = useState(initialTarget?.epicKey || null);

  /* Space (Jira project) — same picker as the Requirement Board. */
  const [spaces, setSpaces] = useState([]);
  const [space, setSpace] = useState(() => spaceOf(initialTarget?.epicKey) || readStoredSpace());
  const [epics, setEpics] = useState([]);
  const [epicsLoading, setEpicsLoading] = useState(false);
  /* Why Jira's epics couldn't be read: the column says it, with Retry, and
     lists nothing (there is no stored copy to fall back on). */
  const [epicsError, setEpicsError] = useState("");
  /* The space whose epics have finished loading. Until it matches the chosen
     space, the guide popup stays up — including the first paint and while the
     space list is still arriving, and even when executions cached from an
     earlier visit already name some epics. */
  const [epicsReadyFor, setEpicsReadyFor] = useState("");
  // The space list has been fetched (successfully or not).
  const [spacesLoaded, setSpacesLoaded] = useState(false);

  /* The selected epic: its saved scripts and its executions. */
  const [tree, setTree] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeRefreshing, setTreeRefreshing] = useState(false);
  const [treeError, setTreeError] = useState("");
  const [epicExecs, setEpicExecs] = useState([]);
  const [epicExecsLoading, setEpicExecsLoading] = useState(false);
  const [capacity, setCapacity] = useState(null);
  const [notice, setNotice] = useState("");
  const [selectedTestId, setSelectedTestId] = useState(
    initialTarget?.storyKey && initialTarget?.testKey ? `${initialTarget.storyKey}|${initialTarget.testKey}` : null,
  );
  /* The story chosen under the epic (NO_STORY for "No story"): the Artifacts
     column shows its Artifacts only, and nothing until one is chosen. The
     selected epic's stories show unless its chevron folded them. */
  const [selectedStoryKey, setSelectedStoryKey] = useState(initialTarget?.storyKey || null);
  const [storiesFolded, setStoriesFolded] = useState(false);

  const load = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && _executionsCache) {
      setExecutions(_executionsCache);
      setLoading(false);
      return;
    }
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}executions`);
      const body = await res.json().catch(() => ({}));
      const data = Array.isArray(body?.data) ? body.data : [];
      _executionsCache = data;
      setExecutions(data);
    } catch {
      setError("Could not load the trial runs.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!selected) load();
  }, [selected, load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}jira/projects`);
        const body = await res.json().catch(() => ({}));
        const list = Array.isArray(body?.data) ? body.data : [];
        if (cancelled) return;
        setSpaces(list);
        /* A space chosen before (or the target's) stays; else PSR (defaultProjectOf). */
        if (list.length) setSpace((cur) => (cur && list.some((p) => p.key === cur) ? cur : defaultProjectOf(list)));
      } catch {
        if (!cancelled) setSpaces([]);
      } finally {
        if (!cancelled) setSpacesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadEpics = useCallback(async (spaceKey) => {
    if (!spaceKey) return;
    setEpicsLoading(true);
    setEpicsError("");
    try {
      const res = await fetch(`${API_BASE_URL}epics?project=${encodeURIComponent(spaceKey)}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Couldn't read the epics of ${spaceKey} from Jira (HTTP ${res.status}).`);
      setEpics(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setEpics([]);
      setEpicsError(e.message || `Couldn't read the epics of ${spaceKey} from Jira.`);
    } finally {
      setEpicsLoading(false);
      setEpicsReadyFor(spaceKey);
    }
  }, []);

  /* Back from a run's page whose trial run was just deleted there: the delete
     read the epic's runs again, and nothing else changed, so the way back
     reads nothing more. Set by confirmDeleteExecution; this effect and the
     epic's (below, so it runs after this one) skip once, and that one clears it. */
  const freshOnReturn = useRef(false);

  useEffect(() => {
    if (!space || selected || freshOnReturn.current) return;
    storeSpace(space);
    loadEpics(space);
  }, [space, selected, loadEpics]);

  /* `quiet`: the Artifacts column's own Refresh — only its icon spins, the
     columns stay as they are until the new tree (and Jira status) arrives. */
  const loadTree = useCallback(async (epicKey, { quiet = false } = {}) => {
    setTreeError("");
    if (!epicKey || epicKey === NO_EPIC) {
      setTree(null);
      return;
    }
    (quiet ? setTreeRefreshing : setTreeLoading)(true);
    try {
      const res = await fetch(`${API_BASE_URL}epics/${encodeURIComponent(epicKey)}/scripts`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setTree(body?.data || null);
      /* The tests' Jira status could not be read: Create stays hidden, say why. */
      if (body?.data?.jira_status_error) setNotice(body.data.jira_status_error);
    } catch (e) {
      if (!quiet) setTree(null);
      setTreeError(`Couldn't load the saved scripts: ${e.message}`);
    } finally {
      (quiet ? setTreeRefreshing : setTreeLoading)(false);
    }
  }, []);

  const loadEpicExecs = useCallback(async (epicKey, { quiet = false } = {}) => {
    if (!epicKey || epicKey === NO_EPIC) {
      setEpicExecs([]);
      return;
    }
    if (!quiet) setEpicExecsLoading(true);
    try {
      const [res, capRes] = await Promise.all([
        fetch(`${API_BASE_URL}epics/${encodeURIComponent(epicKey)}/executions`),
        fetch(`${API_BASE_URL}executions/runs/capacity`),
      ]);
      const body = await res.json().catch(() => ({}));
      const capBody = await capRes.json().catch(() => ({}));
      if (res.ok) setEpicExecs(Array.isArray(body?.data) ? body.data : []);
      if (capRes.ok) setCapacity(capBody?.data || null);
    } catch {
      /* keep what is on screen */
    } finally {
      if (!quiet) setEpicExecsLoading(false);
    }
  }, []);

  /* ---- executions of this space, bucketed by epic ---- */
  const execByEpic = new Map();
  for (const ex of executions) {
    const epicKey = epicOfExecution(ex);
    if (!epicKey) continue;
    const inSpace = spaceOf(epicKey) === space || (ex.tickets || []).some((t) => spaceOf(t.ticket_key) === space);
    if (!inSpace) continue;
    if (!execByEpic.has(epicKey)) execByEpic.set(epicKey, []);
    execByEpic.get(epicKey).push(ex);
  }

  /* ---- sidebar: Jira's epics of the space that hold Artifacts (an epic with
     none, with only executions, or that Jira no longer has isn't listed);
     then the executions under no epic, when there are any. When Jira can't
     be read, nothing: the column says why ---- */
  const epicRows = epics
    .filter((e) => (e.script_count || 0) > 0)
    .map((e) => ({ ...e, execCount: (execByEpic.get(e.key) || []).length }));
  if (!epicsError && execByEpic.has(NO_EPIC))
    epicRows.push({ key: NO_EPIC, summary: "", script_count: 0, execCount: execByEpic.get(NO_EPIC).length });

  /* Searching is the shared BoardSearch (epics, stories and tests across the
     space, answered by the API), so the epic list is never filtered locally. */
  const shownEpics = epicRows;

  /* Only an epic the user clicked (or was sent to) — none on landing, as on
     the Requirement Board, so artifacts and executions load on demand. */
  const effectiveEpicKey =
    selectedEpicKey && epicRows.some((e) => e.key === selectedEpicKey) ? selectedEpicKey : null;
  const currentEpic = epicRows.find((e) => e.key === effectiveEpicKey) || null;
  const isSpecial = effectiveEpicKey === NO_EPIC;
  const bucketExecutions = isSpecial ? execByEpic.get(effectiveEpicKey) || [] : [];
  /* Keep polling while a run is going. (Trial runs send nothing to Jira, so
     nothing arrives after a run ends.) */
  const anyActive = epicExecs.some((ex) => ACTIVE_RUN.has(ex.run_status));

  /* Load the epic's scripts and executions whenever the shown epic changes,
     and on the way back from a run's page — but not after its trial run was
     deleted there: the delete has just read them again (freshOnReturn). */
  useEffect(() => {
    if (selected) return;
    if (freshOnReturn.current) {
      freshOnReturn.current = false;
      return;
    }
    setNotice("");
    loadTree(effectiveEpicKey);
    loadEpicExecs(effectiveEpicKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveEpicKey, selected]);

  /* Live progress: poll while anything in this epic is queued or running. */
  useEffect(() => {
    if (selected || !anyActive) return undefined;
    const t = setInterval(() => loadEpicExecs(effectiveEpicKey, { quiet: true }), 2500);
    return () => clearInterval(t);
  }, [selected, anyActive, effectiveEpicKey, loadEpicExecs]);

  /* A run just finished → refresh the scripts' last results and the sidebar counts. */
  const prevActive = useRef(false);
  useEffect(() => {
    if (prevActive.current && !anyActive) {
      loadTree(effectiveEpicKey);
      _executionsCache = null;
      load(true);
    }
    prevActive.current = anyActive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyActive]);

  /* The Tests-column id (`story|test`, see buildTests) of the test holding
     this Artifact — matched by its script (testcasekey) or its Xray test key —
     or null when it is not saved under this epic. */
  const testIdIn = (t, { storyKey, testKey, testcasekey }) => {
    for (const s of t?.stories || []) {
      if (storyKey && s.key !== storyKey) continue;
      const hit = s.tests.find(
        (x) =>
          (x.scripts || []).length &&
          ((testcasekey && x.scripts.some((sc) => String(sc.testcasekey) === String(testcasekey))) ||
            (testKey && x.key === testKey)),
      );
      if (hit) return `${s.key}|${hit.key || hit.scripts[0].testcasekey}`;
    }
    return null;
  };
  const applyPick = (t, pick) => {
    const id = testIdIn(t, pick);
    if (id) {
      setStoriesFolded(false);
      setSelectedStoryKey(storyOfTest(id));
      setSelectedTestId(id);
    } else setNotice(`${pick.label} isn't saved under ${pick.epicKey} any more.`);
  };

  /* A result picked from the search. An epic selects that epic; an Artifact
     selects its epic and its test; an execution does the same so it shows in
     the Executions column — or, when it belongs to no saved Artifact, opens
     on its own. Tests resolve once that epic's tests are in. */
  const pickFromSearch = (group, item) => {
    setNotice("");
    if (group === "executions" && !(item.epic_key && item.testcasekey)) {
      setSelectedIsRun(false);
      setSelected(item.executionkey);
      return;
    }
    const epicKey = (group === "epics" ? item.key : item.epic_key || "").trim();
    const pick = {
      epicKey,
      storyKey: item.story_key || "",
      testKey: item.xray_test_key || "",
      testcasekey: item.testcasekey || "",
      label: item.key || item.summary || "This artifact",
    };
    if (!epicKey) {
      setNotice(`${pick.label} isn't under an epic, so it isn't listed here.`);
      return;
    }
    if (!epicRows.some((e) => e.key === epicKey)) {
      setNotice(
        epicsError
          ? `The epics couldn't be read from Jira, so ${epicKey} can't be opened. Retry in the Epics column.`
          : epics.some((e) => e.key === epicKey)
            ? `${epicKey} has no Artifacts yet, so it isn't listed here.`
            : spaceOf(epicKey) === space
              ? `${epicKey} is no longer an epic in Jira, so its Artifacts aren't listed here.`
              : `${epicKey} isn't in ${space || "this space"}.`,
      );
      return;
    }
    if (group === "epics") {
      chooseEpic(epicKey);
      return;
    }
    if (epicKey === effectiveEpicKey && tree && !treeLoading) {
      applyPick(tree, pick);
      return;
    }
    /* Different epic: drop its stale tests first, or the Tests column would
       re-select from them before the new epic's tests arrive. */
    setPendingPick(pick);
    setTree(null);
    chooseEpic(epicKey);
  };

  /* An epic clicked (or found): all its Artifacts show, no story chosen. A
     new one starts with its stories open and no Artifact chosen; the same one
     again only clears the story (its chosen Artifact stays). */
  const chooseEpic = (key) => {
    if (key !== effectiveEpicKey) {
      setSelectedTestId(null);
      setStoriesFolded(false);
    }
    setSelectedStoryKey(null);
    setSelectedEpicKey(key);
  };
  /* A story clicked: its Artifacts, none chosen yet (the same one again
     keeps the chosen Artifact). */
  const chooseStory = (key) => {
    if (key !== selectedStoryKey) setSelectedTestId(null);
    setSelectedStoryKey(key);
  };

  useEffect(() => {
    if (!pendingPick || !tree || treeLoading) return;
    if (effectiveEpicKey !== pendingPick.epicKey) return;
    applyPick(tree, pendingPick);
    setPendingPick(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, treeLoading, effectiveEpicKey, pendingPick]);

  /* The epic's tree read again (after a delete, Refresh, an epic chosen):
     its Artifact count follows the tree, without reading Jira again. An epic
     left with none goes from the column (nothing chosen any more); a chosen
     story left with none goes from the tree, so all the epic's Artifacts
     show. A tree still the previous epic's (the new one loading) is skipped. */
  useEffect(() => {
    if (!tree || treeLoading || !effectiveEpicKey || tree.epic?.key !== effectiveEpicKey) return;
    const count = (tree.stories || []).reduce(
      (n, s) => n + (s.tests || []).reduce((m, t) => m + (t.scripts || []).length, 0),
      0,
    );
    setEpics((list) =>
      list.some((e) => e.key === effectiveEpicKey && (e.script_count || 0) !== count)
        ? list.map((e) => (e.key === effectiveEpicKey ? { ...e, script_count: count } : e))
        : list,
    );
    if (count === 0) {
      setSelectedEpicKey(null);
      setSelectedStoryKey(null);
      setSelectedTestId(null);
    } else if (selectedStoryKey && !storyRowsOf(tree).some((s) => s.key === selectedStoryKey)) {
      setSelectedStoryKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, treeLoading, effectiveEpicKey]);

  /* Resolves once the executions and the epic's runs are read again. */
  const reloadEpic = useCallback(() => {
    _executionsCache = null;
    return Promise.all([load(true), loadEpicExecs(effectiveEpicKey, { quiet: true })]);
  }, [load, loadEpicExecs, effectiveEpicKey]);

  /* After a delete: only the epic's runs are read again. The whole list
     (every execution, the slowest read here) is not: what was deleted is
     taken out of it — the trial runs by key, an Artifact's by its scripts
     (as the server picks them: its executions that are not a For you run). */
  const reloadAfterDelete = useCallback(
    ({ executionkeys = [], testcasekeys = [] } = {}) => {
      const runs = new Set(executionkeys.map(String));
      const scripts = new Set(testcasekeys.map(String));
      if (runs.size || scripts.size) {
        setExecutions((list) => {
          const next = list.filter(
            (ex) => !runs.has(String(ex.executionkey)) && !(scripts.has(String(ex.testcasekey)) && !ex.assigned_run),
          );
          _executionsCache = next;
          return next;
        });
      }
      return loadEpicExecs(effectiveEpicKey, { quiet: true });
    },
    [loadEpicExecs, effectiveEpicKey],
  );

  /* Delete confirmed: the dialog closes and the loader lists its steps (the
     delete, with its Log when ticked, then the reload) until the columns are
     fresh; then "X has been deleted successfully." */
  const confirmDeleteExecution = async () => {
    const target = deleteTarget;
    if (!target || deleteBusy) return;
    const log = deleteWithLog;
    setDeleteBusy(true);
    setDeleteTarget(null);
    setRemoving(target);
    removal.start(deleteSteps({ kind: "trial" }, log));
    let deleted = false;
    try {
      const query = [userkey && `userkey=${encodeURIComponent(userkey)}`, log && "with_log=true"].filter(Boolean).join("&");
      const res = await fetch(`${API_BASE_URL}executions/${encodeURIComponent(target.executionkey)}${query ? `?${query}` : ""}`, {
        method: "DELETE",
      });
      if (res.ok) {
        deleted = true;
        removal.mark(0, "done");
      } else {
        const body = await res.json().catch(() => ({}));
        setNotice(typeof body?.detail === "string" ? body.detail : `Could not delete it (HTTP ${res.status}).`);
      }
    } catch (e) {
      setNotice(`Could not delete it: ${e.message}`);
    }
    try {
      removal.mark(1, "doing");
      await reloadAfterDelete({ executionkeys: deleted ? [target.executionkey] : [] });
    } finally {
      setRemoving(null);
      removal.stop();
      setDeleteBusy(false);
    }
    /* Deleted from its own result page: back to the Artifacts, whose runs
       were just read again, so the way back reads nothing more. */
    if (deleted && selected === target.executionkey) {
      freshOnReturn.current = true;
      setSelected(null);
      setSelectedIsRun(false);
    }
    if (deleted) showToast(`${target.name ? `"${target.name}"` : "The trial run"} has been deleted successfully.`);
  };

  const refreshAll = () => {
    load(true);
    loadEpics(space);
    loadTree(effectiveEpicKey);
    loadEpicExecs(effectiveEpicKey);
  };

  /* A trial run's delete: its confirmation, the loader with its steps, then
     the toast — on the board and on a trial run's result page alike. */
  const deleteUi = (
    <>
      {removing && (
        <Loader.Overlay
          variant="guide"
          label={`Deleting ${removing.name ? `"${removing.name}"` : "the trial run"}…`}
          sublabel=""
          delay={0}
        >
          {removal.steps && <DeleteSteps steps={removal.steps} />}
        </Loader.Overlay>
      )}
      {toast}
      {deleteTarget && (
        <ConfirmModal
          title="Delete trial run"
          message={deleteMessage({ kind: "trial", ex: deleteTarget })}
          confirmLabel="Delete"
          irreversible
          option={logOption({ kind: "trial" }, deleteWithLog, setDeleteWithLog)}
          busy={deleteBusy}
          onCancel={() => !deleteBusy && setDeleteTarget(null)}
          onConfirm={confirmDeleteExecution}
        />
      )}
    </>
  );

  /* A run started from a test card opens its clean result page; hand-built
     executions (and ones opened from other pages) keep the full editor. Its
     🗑 deletes it, then goes back to the Artifacts. */
  if (selected && selectedIsRun) {
    return (
      <>
        <ExecutionResultView
          executionkey={selected}
          onBack={() => {
            setSelected(null);
            setSelectedIsRun(false);
          }}
          onDelete={(run) => askDeleteExecution({ executionkey: selected, name: run.name })}
        />
        {deleteUi}
      </>
    );
  }

  if (selected) {
    return (
      <ExecutionDetail
        executionkey={selected}
        userkey={userkey}
        boards={boards}
        autoRun={autoRunKey === selected}
        onAutoRunConsumed={() => setAutoRunKey(null)}
        onBack={() => {
          setSelected(null);
          setAutoRunKey(null);
        }}
      />
    );
  }

  /* Runs of this epic whose script is no longer saved under it (hand-built
     ones, deleted scripts) — still reachable under "Other runs". */
  const treeScriptKeys = new Set((tree?.stories || []).flatMap((s) => s.tests.flatMap((t) => t.scripts.map((sc) => sc.testcasekey))));
  const scriptRuns = epicExecs.filter((ex) => ex.testcasekey && treeScriptKeys.has(String(ex.testcasekey)));
  const otherRuns = epicExecs.filter((ex) => !scriptRuns.includes(ex));
  const storyRows = storyRowsOf(tree);

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col gap-4">
      {/* ── Header ── */}
      <div className="flex flex-none flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Artifacts</h1>

        </div>
        <div className="flex items-center gap-2">
          {capacity && (capacity.running > 0 || capacity.queued > 0) && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700 ring-1 ring-blue-600/20">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
              {capacity.running}/{capacity.max_parallel} running{capacity.queued ? ` · ${capacity.queued} queued` : ""}
            </span>
          )}
        </div>
      </div>

      {/* ── Toolbar: space · search · refresh ── */}
      <div className="flex flex-none flex-wrap items-center gap-2">
        <select
          value={space}
          onChange={(e) => {
            setSpace(e.target.value);
            setSelectedEpicKey(null);
            setSelectedStoryKey(null);
            setSelectedTestId(null);
          }}
          disabled={spaces.length === 0}
          title="Space (Jira project)"
          className="h-[38px] flex-none rounded-lg border border-slate-200 bg-card px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:text-slate-400"
        >
          {spaces.length === 0 && <option value={space}>{space || "Loading spaces…"}</option>}
          {spaces.map((p) => (
            <option key={p.key} value={p.key}>
              {p.key} — {p.name}
            </option>
          ))}
        </select>
        {/* Searches this screen's own columns: epics, Artifacts and
            executions of the space. Picking one selects it here. */}
        <BoardSearch project={space} disabled={!space} onPick={pickFromSearch} scope={ARTIFACT_SCOPE} />
        <button
          type="button"
          onClick={refreshAll}
          disabled={refreshing}
          title="Reload epics, tests and runs"
          className="flex h-[38px] items-center gap-1.5 rounded-lg border border-slate-200 bg-card px-3 text-sm text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
        >
          <Icons.Refresh className={`h-4 w-4 ${refreshing || epicsLoading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && <div className="flex-none rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {/* Only problems are said here (success shows on the cards themselves),
          so in the error style, never as a green tick. */}
      {notice && (
        <div role="alert" className="flex flex-none items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <Icons.Alert className="h-3.5 w-3.5 flex-none" />
          <span className="min-w-0 flex-1">{notice}</span>
          <button type="button" onClick={() => setNotice("")} aria-label="Dismiss" className="rounded p-0.5 hover:bg-red-100">
            <Icons.X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* ── Three levels: epics → tests → runs ── */}
      <div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-card shadow-sm">
        {/* Level 1 — epics of the space */}
        <aside className="flex w-72 flex-none flex-col overflow-hidden border-r border-slate-200 bg-slate-50/40" aria-label="Epics">
          <div className={COLUMN_HEAD} data-testid="column-head">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Icons.JiraEpic /> Epics
              </span>
              <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-500 ring-1 ring-slate-200">{shownEpics.length}</span>
            </div>
          </div>
          <div className="cq-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
            {!space && spacesLoaded ? (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                <p className="text-xs text-slate-400">No Jira space is available. Check the Jira connection.</p>
              </div>
            ) : epicsLoading || !space || epicsReadyFor !== space ? (
              /* The guide popup covers loading the space's epics: on landing,
                 on a space change and on Refresh. */
              <Loader.Overlay
                variant="guide"
                label="Loading epics…"
                sublabel={`Fetching the epics of ${space || "this space"}`}
                delay={0}
              />
            ) : epicsError ? (
              <div role="alert" className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                <Icons.Alert className="h-5 w-5 text-red-500" />
                <p className="text-xs text-red-700">{epicsError}</p>
                <button
                  type="button"
                  onClick={() => loadEpics(space)}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <Icons.Refresh className="h-3 w-3" /> Retry
                </button>
              </div>
            ) : shownEpics.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                <Icons.Search className="h-5 w-5 text-slate-300" />
                <p className="text-xs text-slate-400">No epics with Artifacts in {space || "this space"} yet.</p>
              </div>
            ) : (
              shownEpics.map((e) => (
                <EpicSidebarItem
                  key={e.key}
                  epicKey={e.key}
                  epicSummary={e.summary}
                  status={e.status}
                  statusCategory={e.status_category}
                  priority={e.priority}
                  scriptCount={e.script_count || 0}
                  execCount={e.execCount || 0}
                  selected={effectiveEpicKey === e.key}
                  onClick={() => chooseEpic(e.key)}
                  stories={effectiveEpicKey === e.key && tree && !treeLoading ? storyRows : null}
                  expanded={!storiesFolded}
                  onToggle={() => setStoriesFolded((f) => !f)}
                  selectedStoryKey={selectedStoryKey}
                  onSelectStory={chooseStory}
                  onRefreshStories={() => loadTree(effectiveEpicKey, { quiet: true })}
                  refreshing={treeRefreshing}
                />
              ))
            )}
          </div>
        </aside>

        {/* Levels 2 + 3 — the epic's artifacts, then the clicked artifact's
            executions. Both columns keep their headers before an epic is
            picked; the guide popup covers an epic's load (its artifacts and
            executions come together, so an artifact click is instant). */}
        {currentEpic && !isSpecial && (treeLoading || epicExecsLoading) && (
          <Loader.Overlay
            variant="guide"
            label="Loading artifacts…"
            sublabel={`Fetching the Artifacts and trial runs of ${effectiveEpicKey}`}
            delay={0}
          />
        )}
        {isSpecial ? (
          <div className="cq-scroll min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto">
            {bucketExecutions.map((ex) => (
              <ExecutionListRow key={ex.executionkey} ex={ex} onOpen={(key) => setSelected(key)} onDelete={askDeleteExecution} />
            ))}
          </div>
        ) : treeError ? (
          <div className="m-5 flex h-fit flex-1 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {treeError}
            <button
              type="button"
              onClick={() => loadTree(effectiveEpicKey)}
              className="ml-auto inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 font-semibold text-amber-800 hover:bg-amber-50"
            >
              <Icons.Refresh className="h-3 w-3" /> Retry
            </button>
          </div>
        ) : (
          <TestRunsPanel
            epicKey={effectiveEpicKey}
            noEpic={!currentEpic}
            tree={currentEpic ? tree : null}
            storyKey={selectedStoryKey}
            execs={currentEpic ? scriptRuns : []}
            otherExecs={currentEpic ? otherRuns : []}
            renderOtherRuns={(list) => (
              <div className="divide-y divide-slate-100 bg-card">
                {list.map((ex) => (
                  <ExecutionListRow key={ex.executionkey} ex={ex} onOpen={(key) => setSelected(key)} onDelete={askDeleteExecution} />
                ))}
              </div>
            )}
            capacity={capacity}
                userkey={userkey}
            selectedId={selectedTestId}
            onSelect={setSelectedTestId}
            onOpenRun={(key) => {
              setSelectedIsRun(true);
              setSelected(key);
            }}
            onReload={reloadEpic}
            onDeleted={reloadAfterDelete}
            setNotice={setNotice}
            onRefreshTree={() => loadTree(effectiveEpicKey, { quiet: true })}
          />
        )}
      </div>

      {deleteUi}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Detail: add test cases, run the cycle, record result + evidence    */
/* ------------------------------------------------------------------ */
function ExecutionDetail({ executionkey, userkey, boards, onBack, autoRun = false, onAutoRunConsumed }) {
  const [exec, setExec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [ticketsOpen, setTicketsOpen] = useState(false);
  // Failed test case the "Raise bug" modal is open for (the link row) | null.
  const [bugFor, setBugFor] = useState(null);

  // Runner state.
  const [runningAll, setRunningAll] = useState(false);
  const [paused, setPaused] = useState(false);
  const [currentLink, setCurrentLink] = useState(null); // linkkey running now
  const [progress, setProgress] = useState({}); // linkkey -> { done, total }
  // Per-case steps (the evidence: before/after screenshots the runner captures).
  const [caseSteps, setCaseSteps] = useState({}); // linkkey -> [{ name, status, screenshot, error }]
  const [stepsLoading, setStepsLoading] = useState({}); // linkkey -> bool
  const [expandedCases, setExpandedCases] = useState(() => new Set());
  // Screenshot open in the viewer dialog: { src, caption } | null.
  const [viewShot, setViewShot] = useState(null);
  // Xray push-back state.
  // Follow-up ticket prompt shown after a run completes with failures.
  const [failedPrompt, setFailedPrompt] = useState(null); // { count, names } | null
  const [ticketSummary, setTicketSummary] = useState("");
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [createdTicket, setCreatedTicket] = useState(null); // { key, url } | null
  const [ticketError, setTicketError] = useState("");
  // Xray report retrieval (view the pushed Test Execution's results in-app).
  const [xrayReportOpen, setXrayReportOpen] = useState(false);
  const [xrayReport, setXrayReport] = useState(null);
  const [xrayReportLoading, setXrayReportLoading] = useState(false);
  const [xrayReportError, setXrayReportError] = useState("");
  const wsRef = useRef(null);
  const stopRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}executions/${encodeURIComponent(executionkey)}`);
      const body = await res.json().catch(() => ({}));
      if (res.ok && body?.data) setExec(body.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [executionkey]);

  /* Started with "Run" on the Executions page: the server is running it in
     the background. Follow its progress here and keep the in-page runner off,
     so the same script never runs twice at once. */
  const bgRun = ["queued", "running"].includes(exec?.run_status);
  useEffect(() => {
    if (!bgRun) return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [bgRun, load]);

  // Jira tickets this execution is linked to (shown as chips in the header).
  const [tickets, setTickets] = useState([]);
  const loadTickets = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(executionkey)}/tickets`
      );
      const body = await res.json().catch(() => ({}));
      setTickets(Array.isArray(body?.data) ? body.data : []);
    } catch {
      /* ignore */
    }
  }, [executionkey]);

  useEffect(() => {
    load();
    loadTickets();
  }, [load, loadTickets]);

  // Retrieve the pushed Xray Test Execution's results and show them in-app.
  const viewXrayReport = useCallback(async () => {
    setXrayReportOpen(true);
    setXrayReport(null);
    setXrayReportError("");
    setXrayReportLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(executionkey)}/xray-report`
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setXrayReportError(body?.detail || `HTTP ${res.status}`);
        return;
      }
      setXrayReport(body?.data || null);
    } catch (e) {
      setXrayReportError(`Failed to load: ${e.message}`);
    } finally {
      setXrayReportLoading(false);
    }
  }, [executionkey]);

  // Load a case's steps + its last run's per-step status/screenshots (evidence).
  const fetchSteps = useCallback(async (link) => {
    setStepsLoading((p) => ({ ...p, [link.linkkey]: true }));
    try {
      const res = await fetch(
        `${API_BASE_URL}testcases/${encodeURIComponent(link.testcasekey)}`
      );
      const body = await res.json().catch(() => ({}));
      const tc = body?.data || {};
      const lastSteps = Array.isArray(tc.last_run?.steps) ? tc.last_run.steps : [];
      const v = encodeURIComponent(tc.last_run?.at || "");
      // Persisted before/after evidence for this execution case, by step.
      const evByStep = {};
      (link.step_evidence || []).forEach((se) => {
        const url = `${API_BASE_URL}executions/step-evidence/${encodeURIComponent(
          se.evidencekey
        )}`;
        (evByStep[se.step_index] = evByStep[se.step_index] || {})[se.phase] = url;
      });
      const steps = (Array.isArray(tc.steps) ? tc.steps : []).map((s, i) => {
        const prev = lastSteps[i] || {};
        const ev = evByStep[i + 1] || {};
        return {
          name: describeStep(s),
          status: prev.status || "pending",
          error: prev.error || null,
          before: ev.before || null,
          after:
            ev.after ||
            (prev.screenshot
              ? `${API_BASE_URL}testcases/${encodeURIComponent(
                  link.testcasekey
                )}/steps/${i + 1}/screenshot?v=${v}`
              : null),
        };
      });
      setCaseSteps((p) => ({ ...p, [link.linkkey]: steps }));
      return steps;
    } catch {
      setCaseSteps((p) => ({ ...p, [link.linkkey]: [] }));
      return [];
    } finally {
      setStepsLoading((p) => ({ ...p, [link.linkkey]: false }));
    }
  }, []);

  const toggleExpandCase = useCallback(
    (link) => {
      setExpandedCases((prev) => {
        const next = new Set(prev);
        if (next.has(link.linkkey)) next.delete(link.linkkey);
        else next.add(link.linkkey);
        return next;
      });
      if (!caseSteps[link.linkkey]) fetchSteps(link);
    },
    [caseSteps, fetchSteps]
  );

  // Persist a case's result to the backend + patch local state.
  const putResult = useCallback(
    async (linkkey, result_status) => {
      try {
        await fetch(
          `${API_BASE_URL}executions/${encodeURIComponent(executionkey)}/testcases/${encodeURIComponent(
            linkkey
          )}/result`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ result_status, executed_by: userkey || null }),
          }
        );
        setExec((prev) =>
          prev
            ? {
                ...prev,
                testcases: prev.testcases.map((l) =>
                  l.linkkey === linkkey ? { ...l, result_status } : l
                ),
              }
            : prev
        );
      } catch {
        /* ignore */
      }
    },
    [executionkey, userkey]
  );

  // Run a single test case; resolves with its final status when the WS closes.
  const runOne = useCallback(
    (link) =>
      new Promise((resolve) => {
        (async () => {
          // Make sure we have the step list so we can show live progress +
          // capture the before/after screenshots (the evidence).
          let steps = caseSteps[link.linkkey];
          if (!steps) {
            steps = await fetchSteps(link);
          }
          const total = (steps && steps.length) || link.step_count || 0;
          // Reset every step to pending for a fresh run + auto-expand the case.
          setCaseSteps((p) => ({
            ...p,
            [link.linkkey]: (p[link.linkkey] || []).map((s) => ({
              ...s,
              status: "pending",
              before: null,
              after: null,
              error: null,
            })),
          }));
          setExpandedCases((prev) => new Set(prev).add(link.linkkey));
          setProgress((p) => ({ ...p, [link.linkkey]: { done: 0, total } }));

          let runId = null;
          try {
            const res = await fetch(
              `${API_BASE_URL}testcases/${encodeURIComponent(link.testcasekey)}/run`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  userkey: userkey || null,
                  execution_linkkey: link.linkkey,
                  capture_all: true,
                }),
              }
            );
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body?.data?.run_id) throw new Error(body?.detail || `HTTP ${res.status}`);
            runId = body.data.run_id;
          } catch {
            resolve("failed");
            return;
          }
          const ws = new WebSocket(apiWsUrl(`/api/ws/run/${runId}`));
          wsRef.current = ws;
          let finalStatus = "failed";
          const patchStep = (idx, patch) =>
            setCaseSteps((p) => {
              const arr = (p[link.linkkey] || []).slice();
              if (idx < 0 || idx >= arr.length) return p;
              arr[idx] = { ...arr[idx], ...patch };
              return { ...p, [link.linkkey]: arr };
            });
          ws.onmessage = (evt) => {
            let msg;
            try {
              msg = JSON.parse(evt.data);
            } catch {
              return;
            }
            if (msg.type === "step_start") {
              patchStep(msg.index - 1, { status: "running", error: null });
            } else if (msg.type === "step_evidence") {
              // Before/after screenshot for a step (the evidence).
              patchStep(msg.index - 1, {
                [msg.phase === "before" ? "before" : "after"]: msg.data_url || null,
              });
            } else if (msg.type === "step_result") {
              patchStep(msg.index - 1, {
                status: msg.status === "pass" ? "passed" : "failed",
                error: msg.error || null,
              });
              setProgress((p) => {
                const cur = p[link.linkkey] || { done: 0, total };
                return { ...p, [link.linkkey]: { ...cur, done: cur.done + 1 } };
              });
            } else if (msg.type === "paused") {
              setPaused(true);
            } else if (msg.type === "resumed") {
              setPaused(false);
            } else if (msg.type === "done") {
              finalStatus = msg.status || "failed";
            }
          };
          ws.onclose = () => {
            wsRef.current = null;
            resolve(finalStatus);
          };
          ws.onerror = () => {
            /* onclose still fires */
          };
        })();
      }),
    [userkey, caseSteps, fetchSteps]
  );

  const runAll = useCallback(async () => {
    if (!exec || runningAll) return;
    const links = exec.testcases || [];
    if (!links.length) return;
    stopRef.current = false;
    setRunningAll(true);
    setPaused(false);
    setFailedPrompt(null);
    setCreatedTicket(null);
    setTicketError("");
    // Mark the execution in-progress.
    fetch(`${API_BASE_URL}executions/${encodeURIComponent(executionkey)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "in_progress" }),
    }).catch(() => {});
    const failedLinks = [];
    try {
      for (const link of links) {
        if (stopRef.current) break;
        setCurrentLink(link.linkkey);
        const status = await runOne(link);
        // Map runner outcome -> execution result. Cancelled leaves it as-is.
        if (status === "passed") await putResult(link.linkkey, "passed");
        else if (status === "failed") {
          await putResult(link.linkkey, "failed");
          failedLinks.push(link);
        }
      }
    } finally {
      setCurrentLink(null);
      setRunningAll(false);
      setPaused(false);
      const done = !stopRef.current;
      // A trial: completing the cycle sends nothing to Jira.
      (async () => {
        try {
          await fetch(`${API_BASE_URL}executions/${encodeURIComponent(executionkey)}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: done ? "completed" : "in_progress" }),
          });
        } catch {
          /* status update is best-effort */
        }
        load();
        // After a completed run with failures, surface the follow-up ticket prompt.
        if (done && failedLinks.length > 0) {
          setFailedPrompt({ count: failedLinks.length });
          setTicketSummary(`Re-test: ${exec.name}`);
        }
      })();
    }
  }, [exec, runningAll, executionkey, runOne, putResult, load]);

  // Auto-start the run when opened from the Tickets page "Run" action. Fires
  // once, after the execution (with its test cases) has loaded.
  const autoRunFiredRef = useRef(false);
  useEffect(() => {
    if (
      autoRun &&
      !autoRunFiredRef.current &&
      !loading &&
      exec &&
      (exec.testcases || []).length &&
      !runningAll
    ) {
      autoRunFiredRef.current = true;
      onAutoRunConsumed?.();
      runAll();
    }
  }, [autoRun, loading, exec, runningAll, runAll, onAutoRunConsumed]);

  const pauseRun = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "pause" }));
      } catch {
        /* ignore */
      }
      setPaused(true);
    }
  };
  const resumeRun = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "resume" }));
      } catch {
        /* ignore */
      }
      setPaused(false);
    }
  };
  const stopRun = () => {
    stopRef.current = true;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "cancel" }));
      } catch {
        /* ignore */
      }
    }
    setPaused(false);
  };

  const createFailedTicket = async () => {
    if (!ticketSummary.trim() || creatingTicket) return;
    setCreatingTicket(true);
    setTicketError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(executionkey)}/ticket`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ summary: ticketSummary.trim() }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setCreatedTicket(body.data);
      setFailedPrompt(null);
    } catch (e) {
      setTicketError(e.message || "Failed to create ticket.");
    } finally {
      setCreatingTicket(false);
    }
  };

  const removeCase = async (linkkey) => {
    try {
      await fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(executionkey)}/testcases/${encodeURIComponent(
          linkkey
        )}`,
        { method: "DELETE" }
      );
      load();
    } catch {
      /* ignore */
    }
  };

  const setManualResult = async (linkkey, result_status) => {
    await putResult(linkkey, result_status);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-slate-500">
        <Icons.Spinner className="h-5 w-5 animate-spin" />
        Loading execution…
      </div>
    );
  }
  if (!exec) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <Icons.ArrowLeft className="h-4 w-4" /> Back
        </button>
        <p className="text-sm text-rose-600">Execution not found.</p>
      </div>
    );
  }

  const links = exec.testcases || [];

  return (
    <div className="space-y-5">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
      >
        <Icons.ArrowLeft className="h-4 w-4" /> Executions
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{exec.name}</h1>
            <span
              className={[
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                statusChip(exec.status),
              ].join(" ")}
            >
              {exec.status}
            </span>
          </div>
          {exec.description && <p className="mt-1 text-sm text-slate-500">{exec.description}</p>}
          {bgRun && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-xs text-blue-800 ring-1 ring-blue-600/20">
              <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
              {exec.run_status === "queued"
                ? `Queued${exec.live?.queue_position ? ` #${exec.live.queue_position}` : ""} — starts when a run slot is free.`
                : `Running on the server${exec.live?.total ? ` — step ${exec.live.step}/${exec.live.total}` : ""}. Results appear here when it finishes.`}
            </div>
          )}
          {!bgRun && exec.run_error && (
            <p className="mt-2 text-xs text-rose-600">{exec.run_error}</p>
          )}
          <div className="mt-2 flex items-center gap-1.5 text-xs">
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
              {exec.passed} passed
            </span>
            <span className="rounded-full bg-rose-50 px-2 py-0.5 font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20">
              {exec.failed} failed
            </span>
            <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
              {exec.blocked} blocked
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500">
              {exec.not_run} not run
            </span>
          </div>
          {exec.xray_test_execution_key && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {exec.xray_test_execution_key && (
                <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700 ring-1 ring-inset ring-violet-600/20">
                  {exec.read_only_note || <>Jira: <span className="font-mono">{exec.xray_test_execution_key}</span></>}
                </span>
              )}
            </div>
          )}
          {tickets.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Tickets
              </span>
              {tickets.map((t) => (
                <span
                  key={t.ticketkey}
                  title={t.summary || t.ticket_key}
                  className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20"
                >
                  <span className="h-2 w-2 flex-none rounded-sm bg-purple-500" />
                  <span className="font-mono">{t.ticket_key}</span>
                  {t.summary ? (
                    <span className="max-w-[14rem] truncate text-purple-600/80">
                      {t.summary}
                    </span>
                  ) : null}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Run controls */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            disabled={runningAll}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-card px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
          >
            <Icons.Plus className="h-4 w-4" />
            Add test cases
          </button>

          <button
            type="button"
            onClick={() => setTicketsOpen(true)}
            disabled={runningAll}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-card px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
          >
            <Icons.Boards className="h-4 w-4" />
            Link tickets
          </button>

          {exec.xray_test_execution_key && (
            <button
              type="button"
              onClick={viewXrayReport}
              disabled={runningAll}
              title="Retrieve the execution's results from Jira and view them here"
              className="inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-white px-3 py-2 text-sm font-medium text-violet-700 shadow-sm transition hover:bg-violet-50 disabled:opacity-60"
            >
              <Icons.Report className="h-4 w-4" />
              View Execution Report
            </button>
          )}

          {!runningAll ? (
            <button
              type="button"
              onClick={runAll}
              disabled={!links.length || bgRun || Boolean(exec.read_only_note) || exec.run_mode === "formal"}
              title={
                exec.read_only_note
                  ? `${exec.read_only_note} Read-only.`
                  : exec.run_mode === "formal"
                    ? "A formal run is executed from For you › Test Runs."
                    : bgRun
                      ? "This execution is already running on the server"
                      : `${TRIAL_LABEL}; nothing is written to Jira.`
              }
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
            >
              <Icons.Play className="h-4 w-4" />
              Run execution
            </button>
          ) : (
            <>
              {paused ? (
                <button
                  type="button"
                  onClick={resumeRun}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
                >
                  <Icons.Play className="h-4 w-4" />
                  Resume
                </button>
              ) : (
                <button
                  type="button"
                  onClick={pauseRun}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-card px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <Icons.Pause className="h-4 w-4" />
                  Pause
                </button>
              )}
              <button
                type="button"
                onClick={stopRun}
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-700"
              >
                <Icons.X className="h-4 w-4" />
                Stop
              </button>
            </>
          )}

          <HelpButton screen="executions" />
        </div>
      </div>

      {/* Follow-up ticket prompt — shown after a run completes with failures */}
      {failedPrompt && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <Icons.Alert className="mt-0.5 h-4 w-4 flex-none text-rose-500" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-rose-800">
                  {failedPrompt.count} test case{failedPrompt.count === 1 ? "" : "s"} failed — create a follow-up ticket?
                </p>
                <p className="mt-0.5 text-xs text-rose-600">
                  A new Story will be filed under the same epic in Jira.
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={ticketSummary}
                    onChange={(e) => setTicketSummary(e.target.value)}
                    maxLength={250}
                    placeholder="Ticket summary…"
                    className="min-w-0 flex-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-sm text-slate-800 shadow-sm focus:border-rose-400 focus:outline-none focus:ring-1 focus:ring-rose-400"
                  />
                  <button
                    type="button"
                    onClick={createFailedTicket}
                    disabled={!ticketSummary.trim() || creatingTicket}
                    className="inline-flex flex-none items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:opacity-50"
                  >
                    {creatingTicket ? (
                      <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Icons.Plus className="h-3.5 w-3.5" />
                    )}
                    Create ticket
                  </button>
                </div>
                {ticketError && (
                  <p className="mt-1.5 text-xs text-rose-700">{ticketError}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setFailedPrompt(null)}
              aria-label="Dismiss"
              className="flex-none rounded-md p-1 text-rose-400 hover:bg-rose-100 hover:text-rose-600"
            >
              <Icons.X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Created ticket confirmation */}
      {createdTicket && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Icons.Check className="h-4 w-4 flex-none text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-800">
                Ticket created:{" "}
                {createdTicket.url ? (
                  <a
                    href={createdTicket.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono underline decoration-dotted hover:decoration-solid"
                  >
                    {createdTicket.key}
                  </a>
                ) : (
                  <span className="font-mono">{createdTicket.key}</span>
                )}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setCreatedTicket(null)}
              aria-label="Dismiss"
              className="flex-none rounded-md p-1 text-emerald-400 hover:bg-emerald-100 hover:text-emerald-600"
            >
              <Icons.X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Test cases */}
      <div className="overflow-hidden rounded-2xl bg-card shadow-sm ring-1 ring-slate-200">
        {!links.length ? (
          <div className="px-5 py-12 text-center text-sm text-slate-400">
            No test cases in this execution yet. Use “Add test cases”.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {links.map((link) => {
              const prog = progress[link.linkkey];
              const isCurrent = currentLink === link.linkkey;
              const isOpen = expandedCases.has(link.linkkey);
              const steps = caseSteps[link.linkkey] || [];
              return (
                <li key={link.linkkey} className="px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => toggleExpandCase(link)}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    >
                      <Icons.ChevronRight
                        className={[
                          "h-4 w-4 flex-none text-slate-400 transition-transform",
                          isOpen ? "rotate-90" : "",
                        ].join(" ")}
                      />
                      <span
                        className={[
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                          resultChip(link.result_status),
                        ].join(" ")}
                      >
                        {isCurrent && runningAll ? (
                          <Icons.Spinner className="mr-1 h-3 w-3 animate-spin" />
                        ) : null}
                        {link.result_status}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-slate-900">{link.name}</span>
                        <span className="block text-xs text-slate-400">
                          {link.board_id}
                          {prog
                            ? ` · ${prog.done}/${prog.total} steps`
                            : ` · ${link.step_count} steps`}
                        </span>
                      </span>
                    </button>

                    <div className="flex items-center gap-2">
                      <select
                        value={link.result_status}
                        onChange={(e) => setManualResult(link.linkkey, e.target.value)}
                        disabled={runningAll}
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                      >
                        {RESULT_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>

                      {link.result_status === "failed" && (
                        <button
                          type="button"
                          onClick={() => setBugFor(link)}
                          disabled={runningAll}
                          title="Raise bug"
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 transition hover:bg-rose-100 disabled:opacity-40"
                        >
                          <Icons.Alert className="h-3.5 w-3.5" />
                          Raise bug
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => removeCase(link.linkkey)}
                        disabled={runningAll}
                        title="Remove from execution"
                        className="rounded-md p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-500 disabled:opacity-40"
                      >
                        <Icons.Trash className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Steps + evidence (before/after screenshots the runner captures) */}
                  {isOpen && (
                    <div className="mt-3 ml-7 border-l-2 border-slate-200 pl-4">
                      {stepsLoading[link.linkkey] ? (
                        <div className="flex items-center gap-2 py-2 text-sm text-slate-500">
                          <Icons.Spinner className="h-4 w-4 animate-spin" />
                          Loading steps…
                        </div>
                      ) : !steps.length ? (
                        <div className="py-2 text-sm text-slate-400">
                          This test case has no steps.
                        </div>
                      ) : (
                        <div className="overflow-hidden rounded-lg border border-slate-200">
                          <table className="min-w-full divide-y divide-slate-100 text-sm">
                            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                              <tr>
                                <th className="px-3 py-2 w-10">#</th>
                                <th className="px-3 py-2">Step</th>
                                <th className="px-3 py-2 w-24 text-center">Before</th>
                                <th className="px-3 py-2 w-24 text-center">After</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {steps.map((s, i) => (
                                <tr key={i} className="align-middle">
                                  <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={[
                                          "h-2 w-2 flex-none rounded-full",
                                          stepDot(s.status),
                                        ].join(" ")}
                                      />
                                      <span className="min-w-0 truncate text-slate-700">{s.name}</span>
                                    </div>
                                    {s.error && (
                                      <p className="mt-0.5 pl-4 text-xs text-rose-600">{s.error}</p>
                                    )}
                                  </td>
                                  {["before", "after"].map((phase) => (
                                    <td key={phase} className="px-3 py-2 text-center">
                                      {s[phase] ? (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setViewShot({
                                              src: s[phase],
                                              caption: `${link.name} · Step ${i + 1} · ${
                                                phase === "before" ? "Before" : "After"
                                              }`,
                                            })
                                          }
                                          className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-brand-50 px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
                                        >
                                          <Icons.Eye className="h-3.5 w-3.5" />
                                          View
                                        </button>
                                      ) : (
                                        <span className="text-xs text-slate-300">—</span>
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {addOpen && (
        <AddTestCasesModal
          executionkey={executionkey}
          boards={boards}
          existingKeys={new Set(links.map((l) => l.testcasekey))}
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            setAddOpen(false);
            load();
          }}
        />
      )}

      {viewShot && (
        <ScreenshotDialog
          src={viewShot.src}
          caption={viewShot.caption}
          onClose={() => setViewShot(null)}
        />
      )}

      {ticketsOpen && (
        <ExecutionTicketsModal
          executionkey={executionkey}
          executionName={exec.name}
          userkey={userkey}
          onClose={() => setTicketsOpen(false)}
          onSaved={() => {
            setTicketsOpen(false);
            loadTickets();
          }}
        />
      )}

      {bugFor && (
        <RaiseBugModal
          executionkey={executionkey}
          linkkey={bugFor.linkkey}
          caseName={bugFor.name}
          onClose={() => setBugFor(null)}
          onCreated={() => {}}
        />
      )}

      {xrayReportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className={`flex ${DIALOG_HEIGHT} w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
            <div className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                  <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700">
                    Jira
                  </span>
                  Execution Report
                </h2>
                <p className="mt-0.5 truncate text-sm text-slate-500">
                  {exec.xray_test_execution_key}
                  {xrayReport?.url && (
                    <>
                      {" · "}
                      <a
                        href={xrayReport.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-brand-700 hover:text-brand-800"
                      >
                        Open in Jira
                      </a>
                    </>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setXrayReportOpen(false)}
                className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <Icons.X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {xrayReportLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Icons.Spinner className="h-4 w-4 animate-spin" /> Retrieving from Jira…
                </div>
              ) : xrayReportError ? (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {xrayReportError}
                </div>
              ) : xrayReport ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    {Object.entries(xrayReport.counts || {}).map(([k, v]) => (
                      <span
                        key={k}
                        className={[
                          "rounded-full px-2 py-0.5 font-medium ring-1 ring-inset",
                          k === "PASSED"
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                            : k === "FAILED"
                            ? "bg-rose-50 text-rose-700 ring-rose-600/20"
                            : "bg-slate-100 text-slate-600 ring-slate-500/20",
                        ].join(" ")}
                      >
                        {v} {k}
                      </span>
                    ))}
                  </div>
                  {(xrayReport.tests || []).map((t) => (
                    <div key={t.key} className="rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
                        <span className="min-w-0 truncate text-sm">
                          <span className="font-mono font-semibold text-violet-700">{t.key}</span>{" "}
                          <span className="text-slate-700">{t.summary}</span>
                        </span>
                        <span
                          className={[
                            "flex-none rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                            t.status === "PASSED"
                              ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                              : t.status === "FAILED"
                              ? "bg-rose-50 text-rose-700 ring-rose-600/20"
                              : "bg-slate-100 text-slate-600 ring-slate-500/20",
                          ].join(" ")}
                        >
                          {t.status}
                        </span>
                      </div>
                      <table className="w-full table-fixed text-left text-[11px]">
                        <thead>
                          <tr className="text-slate-400">
                            <th className="w-8 p-2">#</th>
                            <th className="p-2">Action</th>
                            <th className="p-2">Expected</th>
                            <th className="w-20 p-2">Status</th>
                            <th className="w-24 p-2">Evidence</th>
                          </tr>
                        </thead>
                        <tbody className="align-top text-slate-700">
                          {(t.steps || []).map((s, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="p-2 text-slate-400">{i + 1}</td>
                              <td className="p-2 whitespace-pre-wrap">{s.action || "—"}</td>
                              <td className="p-2 whitespace-pre-wrap text-slate-500">{s.result || "—"}</td>
                              <td className="p-2">
                                <span
                                  className={
                                    s.status === "PASSED"
                                      ? "text-emerald-700"
                                      : s.status === "FAILED"
                                      ? "text-rose-700"
                                      : "text-slate-500"
                                  }
                                >
                                  {s.status || "—"}
                                </span>
                              </td>
                              <td className="p-2 text-slate-500">
                                {(s.evidence || []).length
                                  ? `${s.evidence.length} file(s)`
                                  : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  {(!xrayReport.tests || xrayReport.tests.length === 0) && (
                    <p className="text-sm text-slate-500">
                      No test runs found in the Jira execution yet.
                    </p>
                  )}
                  <p className="text-[11px] text-slate-400">
                    Retrieved live from Jira. Evidence files are attached to the test runs — open in
                    Jira to view the screenshots.
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                onClick={() => setXrayReportOpen(false)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Modal: pick a board, then check test cases to add (mixed allowed)  */
/* ------------------------------------------------------------------ */
function AddTestCasesModal({ executionkey, boards, existingKeys, onClose, onAdded }) {
  const [checked, setChecked] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const isSelected = (k) => checked.has(k);

  // Toggle a set of cases as a unit (single case or a whole group).
  const onToggleMany = (caseObjs) =>
    setChecked((prev) => {
      const next = new Set(prev);
      const selectable = caseObjs.filter((c) => !existingKeys.has(c.testcasekey));
      const allSel =
        selectable.length > 0 && selectable.every((c) => next.has(c.testcasekey));
      selectable.forEach((c) => {
        if (allSel) next.delete(c.testcasekey);
        else next.add(c.testcasekey);
      });
      return next;
    });

  const save = async () => {
    if (!checked.size) return;
    setSaving(true);
    try {
      await fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(executionkey)}/testcases`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ testcase_keys: Array.from(checked) }),
        }
      );
      onAdded?.();
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className={`flex ${DIALOG_HEIGHT} w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Add test cases</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <p className="mb-3 text-xs text-slate-400">
            Pick a test suite and choose test cases, categories and groups (groups
            add as a whole). Switch test suites to mix from several. Use the ticket
            filter to add everything linked to a Jira ticket.
          </p>
          <TestCasePicker
            boards={boards}
            isSelected={isSelected}
            onToggleMany={onToggleMany}
            disabledKey={(k) => existingKeys.has(k)}
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Done
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !checked.size}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {saving && <Icons.Spinner className="h-4 w-4 animate-spin" />}
            Add {checked.size || ""}
          </button>
        </div>
      </div>
    </div>
  );
}
